import { useState } from 'react';
import axiosClient from '@/lib/axiosClient';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';
import { useAuthDialog } from '@/context/auth-dialog.context';
import { useQuery } from '@tanstack/react-query';
import { fetcher } from '@/lib/queryClient';
import type { GameLaunchInfo, HomeGame } from '@/types/game';

export function useGameLaunch(game?: HomeGame) {
  const { toast } = useToast();
  const { user } = useAuth();
  const { openDialog: openAuthDialog } = useAuthDialog();
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [showVideo, setShowVideo] = useState(false);
  const [isLaunched, setIsLaunched] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);

  const { refetch: refetchLaunchInfo } = useQuery<GameLaunchInfo>({
    queryKey: ['/api/games', game?.id, 'launches'],
    queryFn: fetcher,
    enabled: false,
  });

  const launch = async () => {
    if (!user) {
      openAuthDialog();
      return;
    }
    if (!game) return;

    setIsLaunching(true);

    if (!game.isFree) {
      const result = await refetchLaunchInfo();
      if (result.isError) {
        toast({
          title: 'Ошибка запуска',
          description: 'Не удалось получить запуски.',
          variant: 'destructive',
        });
        setIsLaunching(false);
        return;
      }
      if (result.data?.remainingLaunches === 0) {
        toast({
          title: 'Ошибка запуска',
          description: 'Запуски закончились',
          variant: 'destructive',
        });
        setIsLaunching(false);
        return;
      }
    }

    if (!game.videoId) {
      setIsLaunching(false);
      return;
    }

    try {
      const res = await axiosClient.get<{ url: string }>(
        `/api/admin/videos/${game.videoId}/signed`,
      );
      setSignedUrl(res.data.url);
      setIsLaunched(true);
      setShowVideo(true);
    } catch (e: any) {
      toast({
        title: 'Ошибка запуска',
        description: e.message || 'Не удалось получить видео.',
        variant: 'destructive',
      });
    } finally {
      setIsLaunching(false);
    }
  };

  return {
    signedUrl,
    isLaunched,
    showVideo,
    isLaunching,
    setShowVideo,
    launch,
  };
}
